'use client';

// `<NetworkModeBanner />` — single sticky banner above the header. Its
// mode mirrors `useNetworkMode()`:
//
//   • mode === 'devnet' → purple "You're in demo mode" banner with a
//                         CTA linking to /about.
//   • mode === 'mainnet' → green "You're in live mode" banner with a
//                          CTA that flips back to demo via
//                          `useSwitchNetwork('devnet')`.
//
// The banner's rendered height is published to the
// `--app-header-sticky-banner-offset` CSS variable. Downstream sticky
// elements (chart vertical-pin overlay, etc.) shift down by that
// amount, and `ResizeObserver` keeps the value fresh as locale-driven
// translation copy reflows the banner height.
import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { useLayoutEffect, useRef } from 'react';
import { ImpersonationBanner } from '@/components/impersonation/impersonation-banner';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Link } from '@/i18n/navigation';
import { useSwitchNetwork } from '@/lib/network/switcher';
import { useNetworkMode } from '@/lib/network/use-network-mode';
import { cn } from '@/lib/ui/utils';
const APP_HEADER_STICKY_BANNER_OFFSET_VAR = '--app-header-sticky-banner-offset';

/** Inline CTA inside one-line banners — tertiary chrome stripped so it reads as linked text. */
export const BANNER_CTA_BASE = 'inline h-auto min-h-0 gap-0 border-0 bg-transparent px-0.5 py-0 text-xs font-medium shadow-none underline underline-offset-2 hover:bg-transparent focus-visible:ring-offset-0' as const;
type BannerVariant = 'demo' | 'live';
function useBannerVariant() {
  return useNetworkMode() === "mainnet" ? "live" : "demo";
}

/** Publishes the banner's rendered height to a CSS variable so sticky
 *  elements below the banner (chart pin overlay, etc.) shift down by
 *  the right amount. `ResizeObserver` keeps it fresh when the banner
 *  reflows due to translation length changes. */
function useBannerHeightSync(bannerRef) {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] !== bannerRef) {
    t0 = () => {
      const root = document.documentElement;
      const el = bannerRef.current;
      if (el === null) {
        return;
      }
      const publish = function publish(target) {
        root.style.setProperty(APP_HEADER_STICKY_BANNER_OFFSET_VAR, `${target.offsetHeight}px`);
      };
      publish(el);
      const observer = new ResizeObserver(() => publish(el));
      observer.observe(el);
      return () => {
        observer.disconnect();
        root.style.setProperty(APP_HEADER_STICKY_BANNER_OFFSET_VAR, "0px");
      };
    };
    t1 = [bannerRef];
    $[0] = bannerRef;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useLayoutEffect(t0, t1);
}
export function NetworkModeBanner() {
  const $ = _c(11);
  const t = useTranslations("header");
  const switchNetwork = useSwitchNetwork();
  const variant = useBannerVariant();
  const bannerRef = useRef(null);
  useBannerHeightSync(bannerRef);
  if (variant === "demo") {
    let t0;
    if ($[0] !== t) {
      t0 = t.rich("demoModeBanner", {
        cta: _temp
      });
      $[0] = t;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    let t1;
    if ($[2] !== t0) {
      t1 = <div ref={bannerRef} className="relative z-sticky w-full bg-[var(--brand-600)] px-4 py-2 text-center text-xs font-medium text-white dark:bg-[var(--brand-400)]">{t0}</div>;
      $[2] = t0;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  let t0;
  if ($[4] !== switchNetwork || $[5] !== t) {
    let t1;
    if ($[7] !== switchNetwork) {
      t1 = chunks_0 => <AnimatedButton type="button" variant="tertiary" size="xs" disableHoverScale={true} onClick={() => switchNetwork("devnet")} className={cn(BANNER_CTA_BASE, "text-feedback-success-on-accent hover:opacity-80 focus-visible:ring-feedback-success-on-accent/30")}>{chunks_0}</AnimatedButton>;
      $[7] = switchNetwork;
      $[8] = t1;
    } else {
      t1 = $[8];
    }
    t0 = t.rich("liveModeBanner", {
      cta: t1
    });
    $[4] = switchNetwork;
    $[5] = t;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  let t1;
  if ($[9] !== t0) {
    t1 = <div ref={bannerRef} className="relative z-sticky min-h-9 w-full bg-feedback-success-accent px-4 py-2 text-center text-xs font-medium text-feedback-success-on-accent">{t0}</div>;
    $[9] = t0;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
}

/** @deprecated Old import name. Rename callers to `NetworkModeBanner`
 *  in `app-header.tsx` and remove this alias. */
function _temp(chunks) {
  return <AnimatedButton asChild={true} variant="tertiary" size="xs" disableHoverScale={true} className={cn(BANNER_CTA_BASE, "text-white hover:text-white hover:opacity-80 focus-visible:ring-white/40")}><Link href="/about">{chunks}</Link></AnimatedButton>;
}
export const DemoModeBanner: typeof NetworkModeBanner = NetworkModeBanner;

/** Renders both the network-mode banner and the engineer impersonation
 *  banner stacked at the top of the app header. Co-located here so the
 *  two top-of-viewport banners are managed in one place. The
 *  impersonation banner short-circuits to `null` when no grant is active. */
export function AppBanners() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <><NetworkModeBanner /><ImpersonationBanner /></>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}